import { useState, useEffect } from 'react';

// Cache persistente compartilhado entre todas as instâncias do hook
const PERSISTENT_CACHE = (() => {
  // Cache em memória global
  let cachedLayers = null;
  let isLoading = false;

  // Constantes
  const CACHE_KEY = 'wms_layers_cache';
  const CACHE_EXPIRATION = 60 * 60 * 1000; // 1 hora

  // URLs padrão
  const DEFAULT_URLS = [
    'https://geoservicos.inde.gov.br/geoserver/ICMBio/ows?service=WMS&version=1.3.0&request=GetCapabilities',
    'https://geoservicos.inde.gov.br/geoserver/MMA/ows?service=WMS&version=1.3.0&request=GetCapabilities',
  ];

  // Callbacks para notificar todos os componentes interessados
  const subscribers = [];

  // Notifica todos os assinantes
  const notifySubscribers = (data) => {
    subscribers.forEach((callback) => callback(data));
  };

  // Carrega dados do localStorage
  const loadCachedData = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (!cachedData) return null;

      const { timestamp, data } = JSON.parse(cachedData);
      const now = Date.now();

      // Verificar expiração
      if (now - timestamp > CACHE_EXPIRATION) {
        return null;
      }

      return data;
    } catch (_e) {
      return null;
    }
  };

  // Salva dados no localStorage
  const saveCachedData = (data) => {
    if (!data || data.length === 0) return;

    try {
      const cache = {
        timestamp: Date.now(),
        data,
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (_e) {
      // Ignora erros de localStorage
    }
  };

  // Carrega dados do servidor
  const fetchData = async (urls = DEFAULT_URLS) => {
    if (isLoading) return;
    isLoading = true;

    try {
      const all = [];

      for (const url of urls) {
        try {
          const res = await fetch(url);
          const text = await res.text();
          const parser = new DOMParser();
          const xml = parser.parseFromString(text, 'application/xml');

          const list = Array.from(xml.getElementsByTagName('Layer'))
            .filter((l) => l.getElementsByTagName('Name').length && !l.querySelector('Layer'))
            .map((l) => ({
              name: l.getElementsByTagName('Name')[0].textContent.trim(),
              title: l.getElementsByTagName('Title')[0].textContent.trim(),
              crsList: Array.from(l.getElementsByTagName('CRS'))
                .map((el) => el.textContent.trim())
                .filter(Boolean),
            }));

          // Processa informações do servidor
          const baseUrl = String(url).split('?')[0];
          let workspace = null;

          try {
            const after = baseUrl.split('/geoserver/')[1] || '';
            workspace = after.split('/')[0] || null;
          } catch (_) {
            workspace = null;
          }

          // Melhora títulos genéricos
          const withBase = list.map((it) => {
            const isGeneric = !it.title || /^GeoServer\s+INDE$/i.test(it.title);
            const friendlyTitle = isGeneric
              ? [workspace, it.name].filter(Boolean).join(' - ')
              : it.title;
            return { ...it, baseUrl, title: friendlyTitle };
          });

          all.push(...withBase);
        } catch (_) {
          // Ignora erros individuais de endpoints
        }
      }

      // Filtra e deduplica resultados
      const filtered = all
        .filter(({ name = '', title = '' }) => {
          const n = String(name).trim();
          const t = String(title).trim();
          return !(
            /^PAN/i.test(n) ||
            /^PAN/i.test(t) ||
            /^Plano/i.test(n) ||
            /^Plano/i.test(t) ||
            /cavidad/i.test(n) ||
            /cavidad/i.test(t)
          );
        })
        .filter(
          (item, idx, arr) =>
            arr.findIndex((x) => `${x.baseUrl}|${x.name}` === `${item.baseUrl}|${item.name}`) ===
            idx,
        );

      // Salva no cache e notifica assinantes
      if (filtered.length > 0) {
        cachedLayers = filtered;
        saveCachedData(filtered);
        notifySubscribers(filtered);
      }

      return filtered;
    } catch (_e) {
      return [];
    } finally {
      isLoading = false;
    }
  };

  // Inicializa dados do cache
  const initializeCache = () => {
    // Primeiro tenta carregar do cache em memória
    if (cachedLayers) {
      return cachedLayers;
    }

    // Depois tenta carregar do localStorage
    const localData = loadCachedData();
    if (localData && localData.length > 0) {
      cachedLayers = localData;
      return localData;
    }

    // Se nenhum cache disponível, retorna vazio
    // (a busca no servidor será feita assincronamente)
    return [];
  };

  // API pública do singleton de cache
  return {
    // Obtém dados - sincronamente do cache ou inicia carga assíncrona
    getData: () => {
      const data = initializeCache();

      // Se não há dados em cache e não está carregando, inicia busca assíncrona
      if ((!data || data.length === 0) && !isLoading) {
        fetchData();
      }

      return data;
    },

    // Registra callback para receber atualizações
    subscribe: (callback) => {
      subscribers.push(callback);
      return () => {
        const index = subscribers.indexOf(callback);
        if (index !== -1) subscribers.splice(index, 1);
      };
    },

    // Força recarregamento dos dados (ignorando cache)
    reload: (urls = DEFAULT_URLS) => {
      return fetchData(urls);
    },
  };
})();

/**
 * Hook para buscar e parsear WMS GetCapabilities.
 * Retorna lista de camadas com { name, title }.
 * Implementa cache para evitar requisições repetidas.
 */
export default function useWmsLayers() {
  // Estado local para camadas
  const [layers, setLayers] = useState(() => PERSISTENT_CACHE.getData());

  useEffect(() => {
    // Assina atualizações do cache global
    const unsubscribe = PERSISTENT_CACHE.subscribe(setLayers);

    // Limpeza ao desmontar
    return unsubscribe;
  }, []);

  return layers;
}
